/**
 * 在途监管平台 — Mock 数据（无后端数据时兜底，保证页面可看可演示）
 */
import type {
  DashboardData,
  EnterpriseDashboardData,
  GPSPoint,
  Paginated,
  QuarantineCert,
  TransitAlert,
  TransitArchive,
  TransitDriver,
  TransitFence,
  TransitOrder,
  TransitVehicle,
  VehiclePosition,
} from '@/types';

const now = new Date();
const iso = (offsetMin: number): string => {
  const d = new Date(now.getTime() - offsetMin * 60000);
  return d.toISOString();
};

export const mockVehicles: TransitVehicle[] = [
  { id: 1001, owner_id: 3001, plate_no: '鲁A12345', vehicle_type: '厢式', capacity_kg: 8000, cattle_capacity: 44, license_no: '370100xxxx', transport_license_no: '鲁交运管鲁字0001', gps_authorized: 1, status: 0, remark: '', created_at: iso(7200) },
  { id: 1002, owner_id: 3001, plate_no: '鲁A67890', vehicle_type: '高栏', capacity_kg: 10000, cattle_capacity: 45, license_no: '370100yyyy', transport_license_no: '鲁交运管鲁字0002', gps_authorized: 1, status: 0, remark: '', created_at: iso(14400) },
  { id: 1003, owner_id: 3001, plate_no: '鲁B55667', vehicle_type: '冷藏', capacity_kg: 9000, cattle_capacity: 43, license_no: '370200zzzz', transport_license_no: '鲁交运管鲁字0003', gps_authorized: 1, status: 0, remark: '', created_at: iso(21600) },
];

export const mockDrivers: TransitDriver[] = [
  { id: 2001, user_id: 5001, owner_id: 3001, name: '张师傅', phone: '13800001001', license_no: '37010219880101xxxx', license_type: 'A2', status: 0, remark: '', created_at: iso(7200) },
  { id: 2002, user_id: 5002, owner_id: 3001, name: '李师傅', phone: '13800001002', license_no: '37010219900101xxxx', license_type: 'B2', status: 0, remark: '', created_at: iso(14400) },
  { id: 2003, user_id: 5003, owner_id: 3001, name: '王师傅', phone: '13800001003', license_no: '37010219850101xxxx', license_type: 'A2', status: 0, remark: '', created_at: iso(21600) },
];

const mkOrder = (id: number, transitNo: string, status: number, vehicleId: number, driverId: number, origin: string, dest: string, quarantine: number, alertLevel: number, lastLng: number, lastLat: number, offsetMin: number): TransitOrder => ({
  id,
  transit_no: transitNo,
  order_id: 0,
  consignor_id: 3001,
  vehicle_id: vehicleId,
  driver_id: driverId,
  origin_city: origin,
  destination_city: dest,
  origin_lng: 117.02,
  origin_lat: 36.65,
  dest_lng: 118.05,
  dest_lat: 36.8,
  cattle_count: 12,
  quarantine_status: quarantine,
  status,
  alert_level: alertLevel,
  last_lng: lastLng,
  last_lat: lastLat,
  last_speed: 62.5,
  last_gps_time: iso(offsetMin),
  archive_hash: '',
  track_hash: '',
  sign_hash: '',
  actual_depart_time: status >= 1 ? iso(offsetMin + 180) : null,
  actual_arrive_time: status >= 2 ? iso(offsetMin - 60) : null,
  sign_photo_url: status >= 3 ? 'https://img.example.com/sign/1.jpg' : '',
  sign_remark: status >= 3 ? '牛只健康，签收完成' : '',
  signed_at: status >= 3 ? iso(offsetMin - 30) : null,
  signed_by: status >= 3 ? 5001 : 0,
  remark: '',
  created_at: iso(offsetMin + 600),
  updated_at: iso(offsetMin),
});

export const mockOrders: TransitOrder[] = [
  mkOrder(1001, 'YNJ202608030012', 1, 1001, 2001, '济南', '青岛', 1, 0, 117.4, 36.58, 5),
  mkOrder(1002, 'YNJ202608030013', 1, 1002, 2002, '济南', '临沂', 1, 2, 117.9, 36.35, 12),
  mkOrder(1003, 'YNJ202608030014', 2, 1003, 2003, '潍坊', '烟台', 1, 0, 119.2, 36.9, 40),
  mkOrder(1004, 'YNJ202608020001', 0, 1001, 2001, '济南', '淄博', 3, 0, 117.02, 36.65, 300),
  mkOrder(1005, 'YNJ202608020002', 3, 1002, 2002, '济南', '德州', 2, 0, 117.2, 36.6, 1500),
  mkOrder(1006, 'YNJ202608010001', 4, 1003, 2003, '济宁', '菏泽', 1, 0, 116.1, 35.9, 3000),
];

const mkAlert = (id: number, transitId: number, vehicleId: number, type: number, level: number, content: string, status: number, offsetMin: number): TransitAlert => ({
  id,
  transit_id: transitId,
  vehicle_id: vehicleId,
  alert_type: type,
  alert_level: level,
  alert_content: content,
  lng: 117.9,
  lat: 36.35,
  handle_status: status,
  handle_by: 0,
  handle_remark: '',
  handled_at: null,
  created_at: iso(offsetMin),
});

export const mockAlerts: TransitAlert[] = [
  mkAlert(3001, 1002, 1002, 2, 2, '车辆偏航，距规划路线 11.2 公里（阈值 10000 米）', 0, 8),
  mkAlert(3002, 1002, 1002, 4, 3, '车厢温湿度连续异常超过 5 分钟，触发温湿度告警', 0, 20),
  mkAlert(3003, 1001, 1001, 6, 1, '运输单 YNJ202608030012 数据中断超过 3 分钟', 2, 35),
  mkAlert(3004, 1005, 1002, 5, 2, '检疫调运单核验不通过：证号-牛只关联无法判定', 1, 60),
  mkAlert(3005, 1004, 1001, 3, 1, '运输单 YNJ202608020001 已到达 24 小时未签收，请尽快处理', 0, 200),
];

const mkFence = (id: number, transitId: number, type: number, name: string, lng: number, lat: number, radius: number, deviation: number): TransitFence => ({
  id,
  transit_id: transitId,
  fence_type: type,
  fence_name: name,
  center_lng: lng,
  center_lat: lat,
  radius_m: radius,
  deviation_threshold_m: deviation,
  polyline: '',
  is_active: 1,
  created_at: iso(500),
});

export const mockFences: TransitFence[] = [
  mkFence(2001, 1001, 1, '济南起运围栏', 117.02, 36.65, 5000, 10000),
  mkFence(2002, 1001, 3, '青岛目的地围栏', 118.05, 36.8, 5000, 10000),
  mkFence(2003, 1002, 1, '济南起运围栏', 117.02, 36.65, 5000, 10000),
];

export const mockCerts: QuarantineCert[] = [
  { id: 4001, transit_id: 1001, cert_type: 1, cert_no: 'A370100202608010001', cert_url: 'https://img.example.com/cert/a1.jpg', cattle_count: 12, verify_status: 1, verify_result: '自动校验通过（证号格式 + 牛只关联）', verify_by: 0, verified_at: iso(200), source: 'platform', created_at: iso(220) },
  { id: 4002, transit_id: 1004, cert_type: 2, cert_no: 'B370100202608020002', cert_url: 'https://img.example.com/cert/b2.jpg', cattle_count: 10, verify_status: 3, verify_result: '证号-牛只关联无法自动判定，转人工复核', verify_by: 0, verified_at: null, source: 'platform', created_at: iso(280) },
  { id: 4003, transit_id: 1005, cert_type: 2, cert_no: 'B370100202608020003', cert_url: 'https://img.example.com/cert/b3.jpg', cattle_count: 8, verify_status: 2, verify_result: '证号格式不合法（应为 A/B 开头 + 至少 6 位数字）', verify_by: 0, verified_at: iso(70), source: 'platform', created_at: iso(90) },
];

export const mockArchives: TransitArchive[] = [
  { id: 5001, transit_id: 1006, transit_no: 'YNJ202608010001', track_hash: 'sha256:9f86d081884c7d65...', cert_hash: 'sha256:2c26b46b68ffc68f...', sign_hash: 'sha256:b94d27b9934d3e08...', tx_hash: '0x7f3a9c2e...', chain_name: 'fabric', block_number: 1203, block_time: iso(2500), status: 1, created_at: iso(2800) },
  { id: 5002, transit_id: 1005, transit_no: 'YNJ202608020002', track_hash: 'sha256:5e884898da280471...', cert_hash: 'sha256:8d969eef6ecad3c2...', sign_hash: 'sha256:8d969eef6ecad3c2...', tx_hash: '', chain_name: '', block_number: 0, block_time: null, status: 0, created_at: iso(1400) },
];

export const mockTrack: GPSPoint[] = (() => {
  const pts: GPSPoint[] = [];
  const count = 36;
  for (let i = 0; i < count; i++) {
    const ratio = i / (count - 1);
    const lng = 117.02 + (118.05 - 117.02) * ratio + Math.sin(i * 0.9) * 0.008;
    const lat = 36.65 + (36.8 - 36.65) * ratio + Math.cos(i * 0.7) * 0.006;
    pts.push({
      id: i + 1,
      transit_id: 1001,
      vehicle_id: 1001,
      lng: Number(lng.toFixed(6)),
      lat: Number(lat.toFixed(6)),
      speed: Number((45 + 25 * Math.sin(i * 0.5)).toFixed(1)),
      direction: 0,
      temperature: 26.5,
      humidity: 55,
      gps_time: iso((count - i) * 1),
    });
  }
  return pts;
})();

const positions: VehiclePosition[] = mockOrders
  .filter((o) => o.status === 1)
  .map((o) => ({
    transit_id: o.id,
    transit_no: o.transit_no,
    plate_no: mockVehicles.find((v) => v.id === o.vehicle_id)?.plate_no ?? '鲁A00000',
    lng: o.last_lng,
    lat: o.last_lat,
    speed: o.last_speed,
    status: 'in_transit',
    alert_level: o.alert_level,
    last_gps_time: o.last_gps_time,
  }));

export function dashboard(): DashboardData {
  return {
    in_transit_count: 2,
    alert_statistics: { today_total: 6, pending_total: 3, yellow_count: 2, orange_count: 1, red_count: 1 },
    vehicle_list: positions,
    recent_orders: mockOrders.slice(0, 5),
  };
}

export function enterpriseDashboard(): EnterpriseDashboardData {
  return {
    in_transit_count: 2,
    today_departed: 3,
    today_arrived: 2,
    pending_sign: 1,
    alert_count: 2,
    vehicle_list: positions,
    recent_orders: mockOrders.slice(0, 5),
  };
}

export function orders(): Paginated<TransitOrder> {
  return { list: mockOrders, total: mockOrders.length, page: 1, page_size: 20 };
}

export function order(id: number): TransitOrder {
  const found = mockOrders.find((o) => o.id === id) ?? mockOrders[0];
  return { ...found, vehicle: mockVehicles.find((v) => v.id === found.vehicle_id), driver: mockDrivers.find((d) => d.id === found.driver_id) };
}

export function alerts(): Paginated<TransitAlert> {
  return { list: mockAlerts, total: mockAlerts.length, page: 1, page_size: 20 };
}

export function track(id: number): GPSPoint[] {
  return mockTrack.map((p) => ({ ...p, transit_id: id }));
}

export function vehicles(): Paginated<TransitVehicle> {
  return { list: mockVehicles, total: mockVehicles.length, page: 1, page_size: 20 };
}

export function drivers(): Paginated<TransitDriver> {
  return { list: mockDrivers, total: mockDrivers.length, page: 1, page_size: 20 };
}

export function fences(transitId: number): TransitFence[] {
  return mockFences.filter((f) => f.transit_id === transitId);
}

export function quarantine(transitId: number): QuarantineCert[] {
  return mockCerts.filter((c) => c.transit_id === transitId);
}

export function archive(transitId: number): TransitArchive | null {
  return mockArchives.find((a) => a.transit_id === transitId) ?? null;
}

/** archivesFor：触发档案上链后的兜底返回（不存在则生成待上链记录） */
export function archivesFor(transitId: number): TransitArchive {
  const found = mockArchives.find((a) => a.transit_id === transitId);
  if (found) return found;
  const order = mockOrders.find((o) => o.id === transitId);
  return {
    id: 5000 + transitId,
    transit_id: transitId,
    transit_no: order?.transit_no ?? `YNJ${transitId}`,
    track_hash: 'sha256:mock-track-hash',
    cert_hash: 'sha256:mock-cert-hash',
    sign_hash: 'sha256:mock-sign-hash',
    tx_hash: `0x${transitId}abc123...`,
    chain_name: 'fabric',
    block_number: 1200 + transitId,
    block_time: new Date().toISOString(),
    status: 1,
    created_at: iso(10),
  };
}
